import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {maybeGetUnitFromCacheBySymbol} from 'inventory/InventoryUnitUtil';
import {fetchUnitBySymbol} from 'inventory/InventoryUnitUtil';
import {StoichiometryVariable} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryEquations';
import StoichiometrySolver, {
  StoichiometryCalculationVariableValues,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometrySolver';
import {
  DEFAULT_MASS_UNIT_SYMBOL,
  DEFAULT_MOL_UNIT_SYMBOL,
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {BaseTypedValueType, CustomEval, CustomEvalResult, TypedValue} from 'notebook/tables/TableConstants';
import {
  coerceToPrimitiveNumber,
  coerceToPrimitiveBoolean,
  isTypedValueEmpty,
  createFloatTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {ResolveQueryFn} from 'notebook/tables/typed-values/TypedValueResolution';
import {StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';

export const OrderedStoichiometryCalculationVariables = Object.values(StoichiometryVariable);

/**
 * Get the factor (from GraphQL cache) to convert a unit reference to another unit.
 *
 * IMPORTANT: GraphQL stores conversion factors relative to SI base units (kg is BaseUnit for mass),
 * but chemistry calculations use g/mol for molecular weight. We need to convert from
 * the unit's SI base (kg) to chemistry standard (g).
 *
 * @param sourceUnitTypedValue - The unit typed value to convert
 * @param targetUnitSymbol - The symbol of the measurement unit to convert to
 * @returns The conversion factor as a number, null if unit cannot be determined, or if the unit
 * is not yet in the cache (triggers a background fetch for subsequent calls).
 */
function getStandardChemistryUnitConversionFactorFromSymbol(
  sourceUnitTypedValue: TypedValue | null,
  targetUnitSymbol: string
): number | null {
  let cachedUnit: MeasurementUnit | null = null;
  if (!sourceUnitTypedValue || sourceUnitTypedValue.type !== BaseTypedValueType.UNIT_REFERENCE) {
    // we can't determine the conversion factor if we don't have a unit reference
    return null;
  }

  const symbol = sourceUnitTypedValue.unitSummary.symbol;
  // try to get unit from graphql cache first, then fetch from server asynchronously if not found
  cachedUnit = maybeGetUnitFromCacheBySymbol(symbol);
  if (cachedUnit) {
    const conversionFactorToSIBase = parseFloat(cachedUnit.conversionFactor);

    // For mass units, we need to convert from kg (SI base) to g (chemistry standard)
    // All mass units have conversionFactor relative to kg, so multiply by 1000 to get grams
    if (targetUnitSymbol === 'g') {
      return conversionFactorToSIBase * 1000; // kg -> g
    }
    return conversionFactorToSIBase;
  }

  // Cache miss - trigger background fetch and return null
  // The caller will handle null as a missing dependency, and subsequent calls
  // will find the unit in the cache once the fetch completes
  fetchUnitBySymbol(symbol);
  return null;
}

export type StoichiometryCalculationCellDependencies = [
  error: TypedValue | null,
  isLimitingReactant: TypedValue | null,
  limitingReactantEquivalence: TypedValue | null,
  limitingReactantMols: TypedValue | null,
  limitingReactantMolsUnit: TypedValue | null,
  equivalence: TypedValue | null,
  molecularWeight: TypedValue | null,
  mols: TypedValue | null,
  molUnit: TypedValue | null,
  amount: TypedValue | null,
  amountUnit: TypedValue | null,
];

function cellDependenciesToStoichiometryCalculationVariableValues(
  cellDependencies: StoichiometryCalculationCellDependencies
): StoichiometryCalculationVariableValues {
  const [
    _error,
    _isLimitingReactant,
    limitingReactantEquivalence,
    limitingReactantMols,
    limitingReactantMolsUnit,
    equivalence,
    molecularWeight,
    mols,
    molUnit,
    amount,
    amountUnit,
  ] = cellDependencies;

  const limitingReactantMolConversion =
    getStandardChemistryUnitConversionFactorFromSymbol(limitingReactantMolsUnit, DEFAULT_MOL_UNIT_SYMBOL) ??
    undefined;
  const molConversion =
    getStandardChemistryUnitConversionFactorFromSymbol(molUnit, DEFAULT_MOL_UNIT_SYMBOL) ?? undefined;
  const amountConversion =
    getStandardChemistryUnitConversionFactorFromSymbol(amountUnit, DEFAULT_MASS_UNIT_SYMBOL) ?? undefined;

  return {
    [StoichiometryVariable.LIMITING_REACTANT_EQUIVALENCE]:
      coerceToPrimitiveNumber(limitingReactantEquivalence) ?? undefined,
    [StoichiometryVariable.LIMITING_REACTANT_MOLS]:
      coerceToPrimitiveNumber(limitingReactantMols) ?? undefined,
    [StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION]: limitingReactantMolConversion,
    [StoichiometryVariable.EQUIVALENCE]: coerceToPrimitiveNumber(equivalence) ?? undefined,
    [StoichiometryVariable.MOLS]: coerceToPrimitiveNumber(mols) ?? undefined,
    [StoichiometryVariable.MOL_UNIT_CONVERSION]: molConversion,
    [StoichiometryVariable.MOLECULAR_WEIGHT]: coerceToPrimitiveNumber(molecularWeight) ?? undefined,
    [StoichiometryVariable.AMOUNT]: coerceToPrimitiveNumber(amount) ?? undefined,
    [StoichiometryVariable.AMOUNT_UNIT_CONVERSION]: amountConversion,
  };
}

export default function makeStoichiometryCalculationCustomEval(solveFor: StoichiometryVariable): CustomEval<{
  tableMetadata: StoichiometryStructuredTableMetadata;
  rowMetadata: StoichiometryStructuredTableRowMetadata;
}> {
  function stoichiometryCustomEval(
    typedValue: TypedValue,
    columnDefinition: ColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ): CustomEvalResult {
    if (cellDependencies.length <= 1 || !isTypedValueEmpty(typedValue)) {
      const errorValue = cellDependencies[0];
      return {
        val: typedValue,
        error: errorValue ? {type: 'ERROR', message: (errorValue as StringTypedValue).rawValue} : null,
        locked: false,
      };
    }
    const isLimitingReactant = coerceToPrimitiveBoolean(cellDependencies[1]) ?? false;
    // The limiting reactant equivalence is locked to 1
    if (isLimitingReactant && solveFor === StoichiometryVariable.EQUIVALENCE) {
      return {
        val: createFloatTypedValue(1),
        locked: true,
        error: null,
      };
    }

    const stoichiometryCalculationVariableValues = cellDependenciesToStoichiometryCalculationVariableValues(
      cellDependencies as StoichiometryCalculationCellDependencies
    );
    const solverResult = StoichiometrySolver.solve(solveFor, stoichiometryCalculationVariableValues);
    // If we are missing any values, don't return an empty value. List out missing dependencies
    // in the error message.
    // eslint-disable-next-line benchlint/no-in-operator
    if ('missingDeps' in solverResult) {
      return {
        val: createFloatTypedValue(null),
        error: {type: 'ERROR', message: `Missing dependencies: ${[...solverResult.missingDeps].join(', ')}`},
        locked: false,
      };
    }
    // Otherwise, return the calculated value and lock the cell.
    const {solution} = solverResult;
    return {
      val: createFloatTypedValue(solution),
      error: null,
      locked: true,
    };
  }
  return stoichiometryCustomEval;
}
